<template>
  <div
    class="HotelExperienceCarouselItem"
    :class="{
      'HotelExperienceCarouselItem--current': current,
      'HotelExperienceCarouselItem--previous': previous,
      'HotelExperienceCarouselItem--next': next,
    }">
    <ai-typo
      class="HotelExperienceCarouselItem-title"
      as="h3"
      :variant="titleVariant">
      {{ title }}
    </ai-typo>

    <ai-typo
      class="HotelExperienceCarouselItem-subtitle"
      as="p"
      variant="paragraph-04-regular">
      {{ subtitle }}
    </ai-typo>

    <div v-if="link" class="HotelExperienceCarouselItem-buttonWrapper">
      <ai-button
        as-link
        :href="link"
        target="_blank"
        variant="tertiary"
        light
        :icon-right="'button-arrow-right' as IconName"
        :label="$t('hotel.card.buttonLabel')"
        :aria-label="$t('hotel.card.buttonLabel', { label: title })"
        slim
        small />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IconName } from '~/domains/ux/atoms/AiIcon/types';
import type AiButton from '~/domains/ux/atoms/AiButton/AiButton.vue';

import type { AiTypoVariant } from '~~/domains/ux/atoms/AiTypo/types';

interface Props {
  title: string;
  subtitle: string;
  current: boolean;
  next: boolean;
  previous: boolean;
  link?: string;
}

const props = defineProps<Props>();

const isSmallDevice = useMatchMediaQuery('tablet');

const titleVariant = computed<AiTypoVariant>(() => {
  if (props.current || isSmallDevice.value) return 'featured';
  if (props.previous || props.next) return 'heading-03';

  return 'heading-04';
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/mq';

.HotelExperienceCarouselItem {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  opacity: 0.25;
  transition: all 500ms;

  @media (mq.$upto-medium) {
    justify-content: flex-start;
    @include mixins.rem-fallback(height, 256);
    @include mixins.rem-fallback(margin-right, -8);
  }

  &--current {
    opacity: 1;
  }

  &--next,
  &--previous {
    opacity: 0.5;
  }
}

.HotelExperienceCarouselItem-buttonWrapper {
  padding-top: 4rem;
}

.HotelExperienceCarouselItem-icon {
  color: colors.$gold-300;
  margin-bottom: constants.$inner-03;
  transform: scale(0);
  max-height: 0;
  transition: all 500ms;

  .HotelExperienceCarouselItem--current & {
    transform: scale(1);
    max-height: 10vh;
  }
}

.HotelExperienceCarouselItem-title {
  color: colors.$gold-400;
  text-align: center;
  line-height: 1;
  transition: all 300ms;
  position: relative;
  width: 100%;

  @media (mq.$from-medium) {
    width: 13ch;
    font-size: 3.8rem;
  }

  @media (mq.$from-large) {
    width: 15ch;
  }

  &::after {
    content: '';
    position: absolute;
    display: block;
    height: 1px;
    background: colors.$gold-500;
    top: 50%;
    width: 0;
    left: -12.8rem;
    z-index: 100;
    transition: all 300ms;
  }

  .HotelExperienceCarouselItem--current & {
    @media (mq.$from-medium) {
      font-size: 5.8rem;
    }
    @media (mq.$from-large) {
      width: 15ch;
      font-size: 7.2rem;
    }
    &::after {
      @media (mq.$from-medium) {
        width: 12.8rem;
      }
    }
  }
}

.HotelExperienceCarouselItem-subtitle {
  text-align: center;
  margin-top: constants.$margin-01;
  color: colors.$white;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  opacity: 0.5;
  transition: all 500ms;

  @media (mq.$from-medium) {
    width: 45ch;
    height: 0;
    transform: scale(0);
    -webkit-line-clamp: unset;
  }

  @media (mq.$from-large) {
    width: 60ch;
  }

  .HotelExperienceCarouselItem--current & {
    opacity: 1;
    height: auto;

    @media (mq.$from-medium) {
      height: auto;
      transform: scale(1);
    }
  }
}
</style>
